import {
    type AdminAgentNoteItemRead,
    type components,
} from '@libraries/typescript/api_client';

type AgentNoteStatus = components['schemas']['AgentNoteStatus'];
type AgentNoteItemOutcome = components['schemas']['AgentNoteItemOutcome'];
type AgentNoteSource = components['schemas']['AgentNoteSource'];
type AgentNoteDomain = components['schemas']['AgentNoteDomain'];
type AgentNoteConfidence = components['schemas']['AgentNoteConfidence'];

export const STATUS_LABELS: Record<AgentNoteStatus, string> = {
    pending: 'Pending',
    classified: 'Classified',
    processed: 'Processed',
    partially_processed: 'Partial',
    fallback: 'Fallback',
    failed: 'Failed',
};

export const SOURCE_LABELS: Record<AgentNoteSource, string> = {
    sms: 'SMS',
    voice: 'Voice',
    voice_post_call: 'Voice post-call',
    sidebar: 'Sidebar',
    slack: 'Slack',
    outreach: 'Outreach',
    automation: 'Automation',
    ui: 'Teach Phoebe',
    sync: 'Sync',
    feedback: 'Feedback',
};

export const DOMAIN_LABELS: Record<AgentNoteDomain, string> = {
    availability: 'Availability',
    memory: 'Memory',
    client_caregiver_preference: 'Client preference',
    attribute: 'Attribute',
    playbook_rule: 'Playbook rule',
    recommendation_default: 'Recommendation default',
    agency_context: 'Agency context',
    outreach_filter: 'Outreach filter',
};

// One fixed hue per domain so chips stay identifiable across the page.
export const DOMAIN_CHIP_CLASSES: Record<AgentNoteDomain, string> = {
    availability:
        'bg-blue-500/10 text-blue-700 dark:bg-blue-400/10 dark:text-blue-300',
    memory: 'bg-violet-500/10 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300',
    client_caregiver_preference:
        'bg-pink-500/10 text-pink-700 dark:bg-pink-400/10 dark:text-pink-300',
    attribute:
        'bg-teal-500/10 text-teal-700 dark:bg-teal-400/10 dark:text-teal-300',
    playbook_rule:
        'bg-orange-500/10 text-orange-700 dark:bg-orange-400/10 dark:text-orange-300',
    recommendation_default:
        'bg-yellow-500/10 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-300',
    agency_context:
        'bg-slate-500/10 text-slate-700 dark:bg-slate-400/10 dark:text-slate-300',
    outreach_filter:
        'bg-rose-500/10 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300',
};

// Fixed hue per source (never cycled) so a source keeps its color across
// every chart; voice channels share a green family deliberately.
export const SOURCE_CHART_COLORS: Record<AgentNoteSource, string> = {
    sms: '#0ea5e9',
    voice: '#10b981',
    voice_post_call: '#14b8a6',
    sidebar: '#6366f1',
    slack: '#a855f7',
    outreach: '#f59e0b',
    automation: '#f97316',
    ui: '#ec4899',
    sync: '#64748b',
    feedback: '#eab308',
};

// Domain chart colors reuse the same hue assignments for the dashboard mix.
export const DOMAIN_CHART_COLORS: Record<AgentNoteDomain, string> = {
    availability: '#3b82f6',
    memory: '#8b5cf6',
    client_caregiver_preference: '#ec4899',
    attribute: '#14b8a6',
    playbook_rule: '#f97316',
    recommendation_default: '#eab308',
    agency_context: '#64748b',
    outreach_filter: '#f43f5e',
};

export const OUTCOME_CHIP_CLASSES: Record<AgentNoteItemOutcome, string> = {
    applied: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    draft_created: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    proposed: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    conflict_notified: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    fallback_memory: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
    unroutable: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
    failed: 'bg-red-500/10 text-red-700 dark:text-red-400',
};

export const OUTCOME_LABELS: Record<AgentNoteItemOutcome, string> = {
    applied: 'Applied',
    conflict_notified: 'Conflict notified',
    draft_created: 'Draft created',
    proposed: 'Proposed',
    fallback_memory: 'Saved to memory',
    unroutable: 'Unroutable',
    failed: 'Failed',
};

export const CONFIDENCE_LABELS: Record<AgentNoteConfidence, string> = {
    high: 'High confidence',
    medium: 'Medium confidence',
    low: 'Low confidence',
};

// The processor writes `fallback_reason` as a machine token, sometimes with a
// `:domain` payload. Rendering the token raw puts `no_entity_signal` in front
// of a human, so the tokens we know about get a sentence here. This map is
// best-effort and nothing enforces it against the Python constants: a renamed
// or new token falls through to the de-underscored token rather than failing,
// so treat a snake_case-looking reason in the panel as a missing entry.
const FALLBACK_REASON_SENTENCES: Record<string, string> = {
    agency_context_disabled:
        'The Agency Guide is turned off for this organization.',
    availability_invalid_windows:
        'The availability windows in the note were not valid.',
    availability_parse_empty: 'No availability windows were found in the note.',
    agency_context_document_disabled:
        'The agency context document is turned off for this organization.',
    entity_unresolved: 'The named person could not be matched to one record.',
    low_confidence: 'The classifier was not confident enough to act on it.',
    no_entity_signal: 'The note named no caregiver or client to attach it to.',
    org_rule_low_confidence:
        'Org-wide rules need high confidence, and this was below it.',
    organization_timezone_missing:
        'The organization has no timezone set, so dates could not be resolved.',
    outreach_filter_mode_conflict:
        'An existing rule for this person uses the opposite filter mode.',
    outreach_filter_unresolved:
        'The outreach filter resolver could not identify a single match.',
    playbook_body_too_large: 'The playbook body exceeded the size limit.',
    playbook_no_eligible_target: 'No playbook was eligible for this rule.',
    playbook_no_matching_target: 'No playbook matched this rule.',
    playbook_parse_failed: 'The playbook rule could not be parsed.',
    processor_not_built: 'No processor is built for this domain yet.',
    preference_unresolved:
        'The preference resolver could not identify a single match.',
    recommendation_parse_failed:
        'The recommendation default could not be parsed.',
};

// Tokens carrying a `:domain` payload, phrased so the domain reads naturally.
const FALLBACK_REASON_WITH_DOMAIN: Record<
    string,
    (domainLabel: string) => string
> = {
    domain_disabled: (domainLabel) =>
        `The ${domainLabel} domain is turned off for this organization.`,
    domain_not_allowed_for_source: (domainLabel) =>
        `The ${domainLabel} domain is not available from this source.`,
};

// A coordinator's "no" is a decision, not a failure, and the status chip
// already says so — repeating it as a reason would read as an error.
const PROPOSAL_REJECTED = 'proposal_rejected';

function isDomain(value: string): value is AgentNoteDomain {
    return value in DOMAIN_LABELS;
}

/** One sentence for a `fallback_reason` token, or null when it carries none. */
export function fallbackReasonSentence(reason: string): string | null {
    if (reason === PROPOSAL_REJECTED) {
        return null;
    }
    const separator = reason.indexOf(':');
    if (separator !== -1) {
        const token = reason.slice(0, separator);
        const payload = reason.slice(separator + 1);
        const phrase = FALLBACK_REASON_WITH_DOMAIN[token];
        if (phrase && isDomain(payload)) {
            return phrase(DOMAIN_LABELS[payload]);
        }
    }
    // An unmapped token still beats nothing: humanise it rather than hiding a
    // reason the pipeline took the trouble to record.
    return FALLBACK_REASON_SENTENCES[reason] ?? reason.replace(/_/g, ' ');
}

export type ItemStatusChip = {
    readonly label: string;
    readonly className: string;
};

const IN_PROGRESS_CHIP_CLASSES =
    'bg-muted text-muted-foreground dark:bg-muted/50';

/**
 * The single chip that says where an item ended up — one verdict, one
 * encoding. Outcome wording is reused from `OUTCOME_LABELS` so this panel and
 * the notes feed never disagree about what a state is called.
 */
export function itemStatusChip(
    item: Pick<
        AdminAgentNoteItemRead,
        'status' | 'outcome' | 'fallback_reason'
    >,
): ItemStatusChip {
    if (item.status === 'pending') {
        return {
            label: 'In progress',
            className: IN_PROGRESS_CHIP_CLASSES,
        };
    }
    if (item.outcome === null) {
        // Terminal without an outcome: older rows predate the column, so the
        // item status is all there is. `fallback` must not read as a clean
        // success — those items were demoted, not applied.
        switch (item.status) {
            case 'failed':
                return {
                    label: OUTCOME_LABELS.failed,
                    className: OUTCOME_CHIP_CLASSES.failed,
                };
            case 'fallback':
                return {
                    label: OUTCOME_LABELS.fallback_memory,
                    className: OUTCOME_CHIP_CLASSES.fallback_memory,
                };
            default:
                return {
                    label: STATUS_LABELS.processed,
                    className: OUTCOME_CHIP_CLASSES.applied,
                };
        }
    }
    if (
        item.outcome === 'failed' &&
        item.fallback_reason === PROPOSAL_REJECTED
    ) {
        return { label: 'Rejected', className: IN_PROGRESS_CHIP_CLASSES };
    }
    return {
        label: OUTCOME_LABELS[item.outcome],
        className: OUTCOME_CHIP_CLASSES[item.outcome],
    };
}

/**
 * The one line explaining why an item did not land where it was asked to, or
 * null when there is nothing to explain.
 *
 * Deliberately NOT gated on a requested/actual domain mismatch. That gate is
 * why a resolver that gave up and captured to memory rendered no reason at
 * all: it keeps its own domain, so the mismatch test is false for exactly the
 * case the reason exists to explain.
 */
export function itemReason(
    item: Pick<
        AdminAgentNoteItemRead,
        | 'status'
        | 'outcome'
        | 'fallback_reason'
        | 'last_error'
        | 'requested_domain'
        | 'domain'
    >,
): string | null {
    const clauses: Array<string> = [];
    if (item.requested_domain !== item.domain) {
        clauses.push(
            `Meant for ${DOMAIN_LABELS[item.requested_domain].toLowerCase()}.`,
        );
    }
    if (item.fallback_reason) {
        const sentence = fallbackReasonSentence(item.fallback_reason);
        if (sentence) {
            clauses.push(sentence);
        }
    }
    if (item.last_error) {
        clauses.push(item.last_error);
    }
    return clauses.length > 0 ? clauses.join(' ') : null;
}
